import {
  CheckOutlined,
  ClockCircleOutlined,
  DownOutlined,
  FireFilled,
  UpOutlined,
} from '@ant-design/icons'
import { Checkbox, Tag, message } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'

// ===== 스타일/타입별 정의 + 타이틀/설명 =====
const VARIANT_STYLES = {
  Today: {
    title: 'NOW / 오늘 업무',
    chipLabel: 'Today',
  },
  Weekly: {
    title: 'NEXT / 예정 업무',
    chipLabel: 'Weekly',
  },
  default: {
    title: '',
    chipLabel: '',
  },
}

// ===== WORK TYPE 헬퍼 함수 =====
const getWorkTypeInfo = (workType) => {
  switch (workType) {
    case 'ROUTINE':
      return {
        displayName: 'Routine',
        className: 'tracker-tag tracker-tag-routine',
        isRoutine: true,
        isFast: false,
        isScheduled: false,
      }
    case 'FAST':
      return {
        displayName: 'Fast',
        className: 'tracker-tag tracker-tag-fast',
        isRoutine: false,
        isFast: true,
        isScheduled: false,
      }
    case 'SCHEDULED':
      return {
        displayName: 'Scheduled',
        className: 'tracker-tag tracker-tag-scheduled',
        isRoutine: false,
        isFast: false,
        isScheduled: true,
      }
    default:
      return {
        displayName: workType || 'Work',
        className: 'tracker-tag tracker-tag-work',
        isRoutine: false,
        isFast: false,
        isScheduled: false,
      }
  }
}

const TrackerBoardList = ({
  titleChip,
  works,
  isLoading,
  hasMore,
  onLoadMore,
  length,
  onCompleteWork,
  onToggleChecklistItem,
  onWorkClick,
  showWatchdogList = false,
  setIsWatchdogPopupOpen,
  setSelectedWatchdogId,
  setIsWatchdogTaskPopupOpen,
  setSelectedTaskId,
  setSelectedTask,
  onRefetch,
}) => {
  // 여러 개의 체크리스트 패널을 동시에 열 수 있도록 id 배열로 관리
  const [openChecklistIds, setOpenChecklistIds] = useState([])
  const variant = VARIANT_STYLES[titleChip] || VARIANT_STYLES.default

  // 외부에서 넘어온 값이 있으면 우선 사용, 없으면 variant 설정 사용
  const headerTitle = variant.title || titleChip
  const headerChipLabel = titleChip || variant.chipLabel

  const hasWorks = works && works.length > 0

  const handleToggleChecklist = (workId) => {
    setOpenChecklistIds((prev) =>
      prev.includes(workId)
        ? prev.filter((id) => id !== workId)
        : [...prev, workId],
    )
  }

  // 체크리스트 항목 체크 토글 (subWork 전체를 전달)
  const handleToggleChecklistItem = (subWork) => {
    onToggleChecklistItem?.(subWork)
  }

  const handleScroll = (event) => {
    if (!hasMore || isLoading) return

    const target = event.currentTarget
    const { scrollTop, clientHeight, scrollHeight } = target

    if (scrollTop + clientHeight >= scrollHeight - 16) {
      if (typeof onLoadMore === 'function') {
        onLoadMore()
      }
    }
  }

  return (
    <div
      className={`tracker-board-container variant-${titleChip.toLowerCase()}`}
    >
      {/* 헤더 영역 */}
      <div
        className="tracker-board-header"
        style={titleChip === 'Weekly' ? { opacity: 0.5 } : undefined}
      >
        <div className="tracker-board-title">
          <span className="tracker-board-label">{headerTitle}</span>
          <span
            className="tracker-board-count"
            style={{ color: titleChip === 'Weekly' ? '#117ffa' : '#3b82f6' }}
          >
            {length}
          </span>
        </div>
        <span className="tracker-board-today-btn">{headerChipLabel}</span>
      </div>

      <div
        className="tracker-board-body"
        onScroll={handleScroll}
        style={titleChip === 'Weekly' ? { opacity: 0.5 } : undefined}
      >
        {/* 컨텐츠 */}
        {!isLoading && !hasWorks && (
          <div
            className="tracker-board-empty"
            style={{
              padding: '120px 0',
            }}
          >
            루틴 업무가 없어요.
          </div>
        )}

        {hasWorks && (
          <div className="tracker-board-list">
            {/* {showWatchdogList && (
              <WatchdogList
                setIsWatchdogPopupOpen={setIsWatchdogPopupOpen}
                setSelectedWatchdogId={setSelectedWatchdogId}
                setIsWatchdogTaskPopupOpen={setIsWatchdogTaskPopupOpen}
                setSelectedTaskId={setSelectedTaskId}
                setSelectedTask={setSelectedTask}
                onRefetch={onRefetch}
              />
            )} */}
            {works.map((work, index) => {
              const checklistItems = Array.isArray(work.todoItems)
                ? work.todoItems
                : []

              const hasSubWorks = checklistItems.length > 0
              const isChecklistOpen = openChecklistIds.includes(work.workItemId)

              const isWorkComplete =
                work.isComplete ?? work.isCompleted ?? work.completed ?? false

              // 모든 체크리스트가 완료되었는지 확인
              const allChecklistCompleted =
                !hasSubWorks ||
                checklistItems.every(
                  (item) =>
                    item.completed ?? item.checked ?? item.isChecked ?? false,
                )
              const workInfo = getWorkTypeInfo(work.workType)
              const isCompleteDisabled =
                (workInfo.isRoutine || workInfo.isScheduled) &&
                hasSubWorks &&
                !allChecklistCompleted

              // 고유한 key 생성: workItemId가 중복될 수 있으므로 인덱스와 조합
              const uniqueKey =
                work.id || `${work.workItemId}-${index}` || `work-${index}`

              return (
                <div className="tracker-board-item" key={uniqueKey}>
                  <div className="tracker-board-item-header">
                    {/* 왼쪽: 태그 + 제목 + 체크리스트 업무 보기 버튼 */}
                    <div className="tracker-board-left">
                      <div className="tracker-board-tags">
                        <span className={workInfo.className}>
                          {workInfo.displayName}
                        </span>
                        {work.hasProblem && (
                          <div className="tracker-board-problem-tag-wrapper">
                            <Tag
                              icon={<FireFilled style={{ color: '#F25A67' }} />}
                              className="tracker-board-problem-tag"
                            ></Tag>
                          </div>
                        )}
                      </div>

                      <div className="tracker-board-header-content">
                        <div className="tracker-board-title-row">
                          <span
                            className="tracker-board-task-title"
                            style={{ cursor: 'pointer' }}
                            onClick={() => onWorkClick?.(work)}
                          >
                            {work.workTitle}
                          </span>
                        </div>

                        {/* ROUTINE & SCHEDULED: 체크리스트 업무 보기 버튼 */}
                        {(workInfo.isRoutine || workInfo.isScheduled) &&
                          hasSubWorks && (
                            <button
                              type="button"
                              className="tracker-board-checklist-btn"
                              onClick={() =>
                                hasSubWorks &&
                                handleToggleChecklist(work.workItemId)
                              }
                              disabled={!hasSubWorks}
                            >
                              체크리스트 업무 보기{' '}
                              {isChecklistOpen ? (
                                <UpOutlined style={{ fontSize: '12px' }} />
                              ) : (
                                <DownOutlined style={{ fontSize: '12px' }} />
                              )}
                            </button>
                          )}

                        {/* FAST: 시간 표시 */}
                        {workInfo.isFast && work.scheduledDateTime && (
                          <span className="tracker-board-fast-time">
                            <ClockCircleOutlined style={{ fontSize: '12px' }} />
                            {dayjs(work.scheduledDateTime).format(
                              'MM/DD HH:mm',
                            )}
                          </span>
                        )}
                      </div>

                      {/* 체크리스트 (ROUTINE & SCHEDULED) - todoItems 에서 표시 */}
                      {(workInfo.isRoutine || workInfo.isScheduled) &&
                        hasSubWorks && (
                          <div
                            className={`tracker-board-checklist-panel ${
                              isChecklistOpen
                                ? 'tracker-board-checklist-panel-open'
                                : ''
                            }`}
                          >
                            <ul className="tracker-board-checklist-list">
                              {checklistItems.map((item, subWorkIndex) => {
                                const isChecked =
                                  item.completed ??
                                  item.checked ??
                                  item.isChecked ??
                                  false
                                const isLastItem =
                                  subWorkIndex === checklistItems.length - 1

                                // onToggleChecklistItem 이 기대하는 형태로 normalize
                                const payload = {
                                  workType: work.workType,
                                  todoItemId: item.id,
                                }

                                return (
                                  <li
                                    key={item.id ?? `sub-${subWorkIndex}`}
                                    className="tracker-board-checklist-row"
                                  >
                                    <div
                                      className={`tracker-board-checklist-item-content ${
                                        isLastItem ? 'no-border' : ''
                                      }`}
                                    >
                                      <Checkbox
                                        checked={isChecked}
                                        disabled={isWorkComplete}
                                        onChange={() =>
                                          handleToggleChecklistItem(payload)
                                        }
                                        className="custom-circle-checkbox"
                                      />
                                      <div
                                        onClick={() => {
                                          if (isWorkComplete) {
                                            message.warning(
                                              '완료된 업무의 체크리스트 상태는 변경할 수 없습니다.',
                                            )
                                            return
                                          }
                                          handleToggleChecklistItem(payload)
                                        }}
                                        className={`tracker-board-checklist-item-title ${
                                          isChecked ? 'checked' : ''
                                        } ${isWorkComplete ? 'disabled' : ''}`}
                                      >
                                        {item.title}
                                      </div>
                                    </div>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        )}
                    </div>

                    {/* 오른쪽: 완료 버튼 */}
                    <div className="tracker-board-right">
                      {isWorkComplete ? (
                        <button
                          className="tracker-board-complete-chip"
                          onClick={() => onCompleteWork?.(work)}
                        >
                          <CheckOutlined style={{ color: '#191A1C' }} />
                          <span style={{ color: '#191A1C' }}>완료됨</span>
                        </button>
                      ) : (
                        <button
                          className={`tracker-board-complete-btn ${isCompleteDisabled ? 'is-disabled' : ''}`}
                          onClick={() => {
                            if (isCompleteDisabled) {
                              message.warning(
                                '체크리스트 업무를 모두 완료한 후에 완료 눌러주세요.',
                              )
                              return
                            }
                            onCompleteWork?.(work)
                          }}
                        >
                          완료
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default TrackerBoardList
